import { useState } from 'react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog } from '@shared/ui';
import type { ProductDetail, ProductStatus } from '@entities/product';
import { useApproveProduct, useRejectProduct } from '../model/use-product-actions';

const statusVariant: Record<ProductStatus, 'secondary' | 'warning' | 'success' | 'destructive'> = {
  DRAFT: 'secondary',
  PENDING_MODERATION: 'warning',
  PUBLISHED: 'success',
  REJECTED: 'destructive',
  ARCHIVED: 'secondary',
};

export function ProductActionsPanel({ product }: { product: ProductDetail }) {
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const approve = useApproveProduct(product.id);
  const reject = useRejectProduct(product.id);

  const canModerate = product.status === 'PENDING_MODERATION';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Статус</span>
          <Badge variant={statusVariant[product.status]} className="text-sm">
            {product.status}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {product.status === 'REJECTED' && product.rejectionReason ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <div className="font-medium text-destructive">Причина отказа</div>
            <p className="mt-1 text-foreground/80">{product.rejectionReason}</p>
          </div>
        ) : null}

        {canModerate ? (
          <div className="flex flex-col gap-2">
            <Button variant="success" onClick={() => setApproveOpen(true)}>
              Approve
            </Button>
            <Button variant="destructive" onClick={() => setRejectOpen(true)}>
              Reject
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Модерация доступна только при PENDING_MODERATION.</p>
        )}
      </CardContent>

      <ConfirmDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        title="Опубликовать товар?"
        description={`«${product.name}» станет доступен покупателям.`}
        confirmLabel="Approve"
        variant="success"
        isPending={approve.isPending}
        onConfirm={() => approve.mutate(undefined, { onSuccess: () => setApproveOpen(false) })}
      />
      <ConfirmDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Отклонить товар?"
        description="Причина будет видна производителю."
        confirmLabel="Reject"
        variant="destructive"
        withReason
        reasonLabel="Причина отказа"
        isPending={reject.isPending}
        onConfirm={(reason) => reject.mutate(reason, { onSuccess: () => setRejectOpen(false) })}
      />
    </Card>
  );
}
